import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { getAuthErrorMessage, loginInputClass } from "../LoginForm";
import { saveButtonClass } from "./ProfilTab";
import { Avatar } from "../ProfileMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("comp_token");

export const ROLES = [
  { id: "vezetobiro", name: "Vezetőbíró" },
  { id: "biro", name: "Bíró" },
  { id: "stilusbiro", name: "Stílusbíró" },
  { id: "dijlovasbiro", name: "Díjlovas bíró" },
  { id: "lovastusabiro", name: "Lovastusa bíró" },
  { id: "melegitobiro", name: "Melegítőpálya bíró" },
  { id: "versenyszervezo", name: "Versenyszervező" },
  { id: "palyaepito", name: "Pályaépítő" },
  { id: "versenyiroda", name: "Versenyiroda" },
];
export const START_PERMISSIONS = ["ALAP REV", "REV", "GLAV"];

const ACCOUNT_ROLES = [["admin", "Admin"], ["edzo", "Edző"], ["normal", "Normál"]];
const accountRoleOf = (u) => u.account_role || (u.is_admin ? "admin" : "normal");
const RoleRadios = ({ value, onChange }) => (
  <div className="rounded-2xl bg-slate-50 p-4" data-testid="account-role-column">
    <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-600">Szerepkör</h3>
    <div className="space-y-2">
      {ACCOUNT_ROLES.map(([id, name]) => (
        <label key={id} className="flex cursor-pointer items-center gap-3 rounded-xl bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm hover:bg-sky-50">
          <input type="radio" name="account-role" value={id} checked={value === id} onChange={() => onChange(id)} className="h-5 w-5 accent-sky-600" data-testid={`account-role-${id}`} />
          {name}
        </label>
      ))}
    </div>
  </div>
);

const CheckList = ({ title, items, selected, onToggle, prefix }) => (
  <div className="rounded-2xl bg-slate-50 p-4" data-testid={`${prefix}-column`}>
    <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-600">{title}</h3>
    <div className="space-y-2">
      {items.map((it) => (
        <label key={it.id} className="flex cursor-pointer items-center gap-3 rounded-xl bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm hover:bg-sky-50">
          <input type="checkbox" checked={selected.includes(it.id)} onChange={() => onToggle(it.id)} className="h-5 w-5 rounded-md accent-sky-600" data-testid={`${prefix}-${it.id.replace(/\s+/g, "-").toLowerCase()}`} />
          {it.name}
        </label>
      ))}
    </div>
  </div>
);

export function JogosultsagokTab() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [accountRole, setAccountRole] = useState("normal");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/competition/users`, { params: { token: token() } });
      setUsers(res.data || []);
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
      setUsers([]);
    }
  };
  useEffect(() => { load(); }, []);

  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return [...(users || [])]
      .filter((u) => !n || (u.name || "").toLowerCase().includes(n) || (u.identifier || "").toLowerCase().includes(n))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "hu"));
  }, [users, q]);

  const open = (u) => { setSelected(u); setRoles(u.roles || []); setPerms(u.start_permissions || []); setAccountRole(accountRoleOf(u)); };
  const toggle = (arr, set) => (id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/competition/users/${selected.id}/roles`, { roles, start_permissions: perms, account_role: accountRole }, { params: { token: token() } });
      toast.success("Jogosultságok elmentve");
      const patch = { roles, start_permissions: perms, account_role: accountRole, is_admin: accountRole === "admin" };
      setUsers((us) => us.map((u) => (u.id === selected.id ? { ...u, ...patch } : u)));
      setSelected((s) => ({ ...s, ...patch }));
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
    setSaving(false);
  };

  const card = "rounded-3xl bg-white p-5 sm:p-7 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100";

  if (selected) {
    return (
      <div className={`mx-auto w-full max-w-4xl ${card}`} data-testid="permissions-editor">
        <button type="button" onClick={() => setSelected(null)} className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:text-sky-900" data-testid="permissions-back-btn">
          <ArrowLeft size={16} /> Vissza a listához
        </button>
        <div className="mb-6 flex items-center gap-3">
          <Avatar user={selected} className="h-12 w-12" />
          <div>
            <h2 className="text-lg font-bold text-slate-900" data-testid="permissions-user-name">{selected.name}</h2>
            <p className="font-mono text-sm font-semibold tracking-wider text-slate-700" data-testid="permissions-user-identifier">{selected.identifier || "—"}</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <CheckList title="Jogosultságok" items={ROLES} selected={roles} onToggle={toggle(roles, setRoles)} prefix="role" />
          <CheckList title="Rajtengedélyek" items={START_PERMISSIONS.map((p) => ({ id: p, name: p }))} selected={perms} onToggle={toggle(perms, setPerms)} prefix="perm" />
          <RoleRadios value={accountRole} onChange={setAccountRole} />
        </div>
        <button type="button" onClick={save} disabled={saving} className={`${saveButtonClass} mt-6`} data-testid="permissions-save-btn">{saving ? "Mentés..." : "Mentés"}</button>
      </div>
    );
  }

  return (
    <div className={`mx-auto w-full max-w-4xl ${card}`} data-testid="jogosultsagok-tab">
      <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900"><ShieldCheck size={20} className="text-slate-700" /> Rajtengedély / Jogosultságok</h2>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés név vagy azonosító alapján" className={`${loginInputClass} mb-4`} data-testid="permissions-search-input" />
      {!users ? <p className="py-8 text-center text-slate-600">Betöltés...</p> : (
        <ul className="divide-y divide-slate-100" data-testid="permissions-users-list">
          {list.map((u) => (
            <li key={u.id}>
              <button type="button" onClick={() => open(u)} className="-mx-2 flex w-full items-center gap-3 rounded-xl px-2 py-3 text-left hover:bg-slate-50" data-testid={`permissions-user-${u.id}`}>
                <Avatar user={u} className="h-10 w-10 shrink-0" />
                <span className="font-semibold text-slate-800">{u.name}</span>
                <span className="font-mono text-xs font-semibold tracking-wider text-slate-700">{u.identifier || "—"}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default JogosultsagokTab;
